import { mapFileDatum } from "../../../constants/map/file";
import { get } from "lodash";
import {
  mapOrganizationMembers,
  mapOrganizationRoles,
} from "../../../constants/map/organization";
import { getRoleIdFromName } from "../../../constants/organizationRoles";

export const organizationReducer = (state, action) => {
  switch (action.type) {
    case "fetchOrganization": {
      // Handle both new and old API formats
      if (action.payload?.payload) {
        // New API format
        const org = Array.isArray(action.payload.payload.data)
          ? action.payload.payload.data[0]
          : action.payload.payload;

        const documents = org?.documents || [];

        // Remove duplicates based on document ID
        const documentsMap = new Map();
        documents.forEach((doc) => {
          if (!documentsMap.has(doc.id)) {
            documentsMap.set(doc.id, doc);
          }
        });

        const uniqueDocuments = Array.from(documentsMap.values());
        const documentsIds = uniqueDocuments.map((doc) => doc.id);
        const mappedDocuments = uniqueDocuments.map((doc) => ({
          uuid: doc.id,
          id: doc.id,
          fileName: doc.fileName, // Use correct property name
          fileSize: doc.fileSize || 0, // Add fileSize with fallback
          // Create a data URL from base64 if fileData exists, otherwise use download endpoint
          url: doc.fileData
            ? `data:application/octet-stream;base64,${doc.fileData}`
            : `/Attachment/download/${doc.id}`,
        }));

        return {
          ...state,
          fieldDocumentsIds: documentsIds,
          fieldDocuments: mappedDocuments,
        };
      } else {
        // Old API format
        const { data, included } = action.payload;
        const documentsIds = [];
        const documents = mapFileDatum(
          get(data, "relationships.field_documents"),
          included
        );
        documents &&
          documents.map((document) => documentsIds.push(document.uuid));
        return {
          ...state,
          fieldDocumentsIds: documentsIds,
          fieldDocuments: documents ? documents : [],
        };
      }
    }
    case "organizationMember": {
      const { included } = action.payload;
      if (!included) return { ...state };
      const dataRoles = included.filter(
        (x) => x.type === "group_role--group_role"
      );
      const mappedRoles = mapOrganizationRoles({
        data: dataRoles,
        included: included,
      });
      return {
        ...state,
        roles: Object.values(mappedRoles),
        organizationMembers: mapOrganizationMembers(action.payload),
      };
    }
    case "organizationMemberFromNewAPI": {
      const members = action.payload;
      if (!members) return { ...state };

      // Map members from new API format to expected format
      const mappedMembers = members.reduce((acc, member) => {
        const memberData = {
          uuid: member.userId,
          id: member.id,
          email: member.email,
          status: member.status === true ? "active" : "pending_invitation",
          membershipUuid: member.id,
          userName: member.name || member.email || "Unknown",
          created: member.created ? new Date(member.created) : new Date(),
          changed: member.changed ? new Date(member.changed) : new Date(),
          invitationExpirationDate: member.invitationExpirationDate
            ? new Date(member.invitationExpirationDate)
            : null,
          // Map organizationRoles array to role IDs
          roles: member.organizationRoles
            ? member.organizationRoles.map((role) => {
                const roleName = role.name || role.title || role.id;
                return getRoleIdFromName(roleName) || roleName;
              })
            : [],
        };
        acc[memberData.membershipUuid] = memberData;
        return acc;
      }, {});

      return {
        ...state,
        organizationMembers: mappedMembers,
      };
    }
    case "organizationApplicationsFromNewAPI": {
      const applications = action.payload;
      if (!applications) return { ...state };

      // Store applications from new API format
      return {
        ...state,
        organizationApplications: applications,
      };
    }
    case "organizationRoles": {
      // Handle both new and old API formats
      if (action.payload?.payload?.data) {
        // New API format
        const roles = action.payload.payload.data;
        const mappedRoles = roles.map((role) => ({
          uuid: role.id,
          id: getRoleIdFromName(role.name || role.title) || role.id,
          permissions: role.permissions || [],
        }));

        const rolesById = mappedRoles.reduce((acc, role) => {
          acc[role.id] = role;
          return acc;
        }, {});

        return {
          ...state,
          roles: mappedRoles,
          rolesById: rolesById,
        };
      } else {
        // Old API format
        const { data } = action.payload;
        if (!data) return { ...state };
        const mappedRoles = mapOrganizationRoles(action.payload);
        return {
          ...state,
          roles: Object.values(mappedRoles || {}),
          rolesById: mappedRoles || {},
        };
      }
    }
    case "updateOrganization": {
      // Handle both new and old API formats
      if (action.payload?.payload) {
        // New API format - use the same logic as fetchOrganization
        const org = action.payload.payload;
        const documents = org?.documents || [];

        // Remove duplicates based on document ID
        const documentsMap = new Map();
        documents.forEach((doc) => {
          if (!documentsMap.has(doc.id)) {
            documentsMap.set(doc.id, doc);
          }
        });

        const uniqueDocuments = Array.from(documentsMap.values());
        const documentsIds = uniqueDocuments.map((doc) => doc.id);
        const mappedDocuments = uniqueDocuments.map((doc) => ({
          uuid: doc.id,
          id: doc.id,
          fileName: doc.fileName,
          fileSize: doc.fileSize || 0,
          url: doc.fileData
            ? `data:application/octet-stream;base64,${doc.fileData}`
            : `/Attachment/download/${doc.id}`,
        }));

        return {
          ...state,
          fieldDocumentsIds: documentsIds,
          fieldDocuments: mappedDocuments,
        };
      } else {
        // Old API format
        const { included, data } = action.payload;
        const documents = mapFileDatum(
          get(data, "relationships.field_documents"),
          included
        );
        const documentsIds = [];
        documents &&
          documents.map((document) => documentsIds.push(document.uuid));
        return {
          ...state,
          fieldDocumentsIds: documentsIds,
          fieldDocuments: documents ? documents : [],
        };
      }
    }
    case "createOrganizationMembership": {
      const { data, included } = action.payload;
      if (!data) return { ...state };
      const invitedMember = mapOrganizationMembers({
        data: [data],
        included,
      });
      return {
        ...state,
        organizationMembers: { ...state.organizationMembers, invitedMember },
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
};
